import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import {
  companyCreateInputSchema,
  companyTypeEnum,
  type CompanyCreateInput,
} from "@/schemas";
import { useCreateCompany } from "@/hooks/useCompanies";
import {
  useCompaniesHouseSearch,
  useFetchCompaniesHouseProfile,
  type CompaniesHouseSearchItem,
  type CompaniesHouseProfile,
} from "@/hooks/useCompaniesHouse";
import { currentBrand } from "@/lib/brand";

const COUNTRY_OPTIONS = [
  { value: "GB", label: "United Kingdom" },
  { value: "US", label: "United States" },
  { value: "PT", label: "Portugal" },
];

const COMPANY_TYPE_LABELS: Record<string, string> = {
  limited: "Limited company",
  sole_trader: "Sole trader",
  partnership: "Partnership",
  llc: "LLC",
  s_corp: "S corp",
  freelancer: "Freelancer",
  lda: "Lda",
};

export default function CompanyNew() {
  const navigate = useNavigate();
  const create = useCreateCompany();
  const brand = currentBrand();

  const form = useForm<CompanyCreateInput>({
    resolver: zodResolver(companyCreateInputSchema),
    defaultValues: {
      name: "",
      country: "GB",
      company_type: "limited",
      registration_number: "",
      financial_year_end_month: 3,
      financial_year_end_day: 31,
      vat_registered: false,
      vat_number: "",
      currency: "GBP",
      sic_code: "",
      status: "active",
      source_site_slug: brand.sourceSlug,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const company = await create.mutateAsync(values);
      navigate(`/dashboard/app/companies/${company.id}`, { replace: true });
    } catch (err) {
      form.setError("name", {
        message: (err as Error)?.message ?? "Could not create company.",
      });
    }
  });

  const country = form.watch("country");
  const companyType = form.watch("company_type");
  const showLookup = country === "GB" && companyType === "limited";

  return (
    <section className="mx-auto max-w-xl">
      <header>
        <Link
          to="/dashboard/app/companies"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Companies
        </Link>
        <h1 className="mt-3 text-3xl font-semibold">Add a company</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          We use this to attach your FreeAgent connection and build the dashboard
          you can share with your accountant.
        </p>
      </header>

      {showLookup ? (
        <CompaniesHouseLookup
          onPicked={(profile) => {
            form.setValue("name", profile.company_name ?? "", {
              shouldDirty: true,
              shouldValidate: true,
            });
            form.setValue("registration_number", profile.company_number ?? "", {
              shouldDirty: true,
            });
            if (profile.sic_codes?.[0]) {
              form.setValue("sic_code", profile.sic_codes[0], { shouldDirty: true });
            }
            if (profile.accounts?.accounting_reference_date) {
              const m = parseInt(
                profile.accounts.accounting_reference_date.month ?? "",
                10,
              );
              const d = parseInt(
                profile.accounts.accounting_reference_date.day ?? "",
                10,
              );
              if (!Number.isNaN(m) && m >= 1 && m <= 12) {
                form.setValue("financial_year_end_month", m, { shouldDirty: true });
              }
              if (!Number.isNaN(d) && d >= 1 && d <= 31) {
                form.setValue("financial_year_end_day", d, { shouldDirty: true });
              }
            }
          }}
        />
      ) : null}

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <Field
          label="Company name"
          error={form.formState.errors.name?.message}
          input={
            <input
              autoFocus={!showLookup}
              className="w-full rounded-md border bg-background px-3 py-2"
              {...form.register("name")}
            />
          }
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Country"
            input={
              <select
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("country")}
              >
                {COUNTRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            }
          />
          <Field
            label="Company type"
            input={
              <select
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("company_type")}
              >
                {companyTypeEnum.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {COMPANY_TYPE_LABELS[opt] ?? opt}
                  </option>
                ))}
              </select>
            }
          />
        </div>

        <Field
          label="Registration number"
          hint={
            showLookup
              ? "Auto-filled when you pick a company above. Or type your 8-digit Companies House number."
              : "Optional — leave blank if not applicable."
          }
          input={
            <input
              className="w-full rounded-md border bg-background px-3 py-2"
              placeholder="e.g. 12345678"
              {...form.register("registration_number")}
            />
          }
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Financial year-end month"
            input={
              <input
                type="number"
                min={1}
                max={12}
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("financial_year_end_month", { valueAsNumber: true })}
              />
            }
          />
          <Field
            label="Financial year-end day"
            input={
              <input
                type="number"
                min={1}
                max={31}
                className="w-full rounded-md border bg-background px-3 py-2"
                {...form.register("financial_year_end_day", { valueAsNumber: true })}
              />
            }
          />
        </div>

        <Field
          label="Currency"
          input={
            <select
              className="w-full rounded-md border bg-background px-3 py-2"
              {...form.register("currency")}
            >
              <option value="GBP">GBP — British pound</option>
              <option value="USD">USD — US dollar</option>
              <option value="EUR">EUR — Euro</option>
            </select>
          }
        />

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...form.register("vat_registered")} />
          VAT registered
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create company"}
          </button>
          <Link
            to="/dashboard/app/companies"
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-secondary"
          >
            Cancel
          </Link>
        </div>
      </form>
    </section>
  );
}

/**
 * Debounced Companies House search panel. Calls the Sidequester proxy
 * endpoint, shows results inline, and yields a full profile back to the
 * caller when one is picked.
 */
function CompaniesHouseLookup({
  onPicked,
}: {
  onPicked: (profile: CompaniesHouseProfile) => void;
}) {
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  const [pickedNumber, setPickedNumber] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const search = useCompaniesHouseSearch(pickedNumber ? "" : debounced);
  const fetchProfile = useFetchCompaniesHouseProfile();

  const items = useMemo<CompaniesHouseSearchItem[]>(
    () => search.data?.items ?? [],
    [search.data],
  );

  const handlePick = async (item: CompaniesHouseSearchItem) => {
    setPickedNumber(item.company_number);
    setInput(item.title);
    try {
      const profile = await fetchProfile.mutateAsync(item.company_number);
      onPicked(profile);
    } catch {
      // Surface a soft error inline — user can still fill the form by hand.
    }
  };

  return (
    <div className="mt-8 rounded-lg border bg-secondary/30 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Look up on Companies House</div>
          <p className="text-xs text-muted-foreground">
            Search by name or 8-digit number to pre-fill the form.
          </p>
        </div>
        {pickedNumber ? (
          <button
            type="button"
            onClick={() => {
              setPickedNumber(null);
              setInput("");
            }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Change
          </button>
        ) : null}
      </div>

      <div className="mt-3">
        <input
          type="text"
          value={input}
          onChange={(e) => {
            if (pickedNumber) setPickedNumber(null);
            setInput(e.target.value);
          }}
          placeholder="e.g. Sidequester or 12345678"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        />
      </div>

      {!pickedNumber && debounced.length >= 2 ? (
        <div className="mt-3 max-h-60 overflow-y-auto rounded-md border bg-background">
          {search.isLoading ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>
          ) : search.isError ? (
            <div className="px-3 py-2 text-sm text-destructive">
              Search failed.{" "}
              {(search.error as Error)?.message?.includes("COMPANIES_HOUSE")
                ? "Companies House API key isn't configured yet."
                : "Try again in a moment."}
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No matches.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((item) => (
                <li key={item.company_number}>
                  <button
                    type="button"
                    onClick={() => handlePick(item)}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
                  >
                    <div className="font-medium">{item.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.company_number}
                      {item.company_status ? ` · ${item.company_status}` : ""}
                      {item.address_snippet ? ` · ${item.address_snippet}` : ""}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {fetchProfile.isPending ? (
        <p className="mt-2 text-xs text-muted-foreground">Fetching company details…</p>
      ) : pickedNumber && !fetchProfile.isError ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Filled from Companies House. You can edit any field below.
        </p>
      ) : null}
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  input,
}: {
  label: string;
  hint?: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      {input}
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
